/* eslint-disable camelcase */
import { Location, ReportTaskStatus } from '@nx-smartmonkey/shared/domain';
import * as XLSX from 'xlsx';

import { Project } from '../../domain/project/Project';
import { Stop } from '../../domain/stop/Stop';
import { TaskStatus } from '../../domain/task/TaskStatus';
import { intl } from '../IntGlobalProvider';
import { ExcelEntryFormatter, customFieldToFormatter, jsonToExcel } from './Formatters';

export type StopsTasksColumnKeys = `label` | `location` | `location_details` | `external_id` | `comments`;

interface StopTaskEntry {
  stop: {
    id: string;
    label?: string;
    location?: Location | null;
    location_details?: string;
    external_id?: string;
    comments?: string;
  };
  task: {
    label?: string;
    comments?: string;
    barcode?: string;
    status: TaskStatus;
    custom_fields?: Record<string, any>;
    report?: {
      task_id: string;
      status: ReportTaskStatus;
      custom_fields?: Record<string, any>;
    };
  };
}

type ExportColumn<T> = {
  identifier: T;
  displayable: boolean;
};

export type StopsTasksColumn = ExportColumn<StopsTasksColumnKeys>;

const getStopFormatters = (parsedTableColumns: Record<StopsTasksColumnKeys, boolean>): ExcelEntryFormatter[] => {
  const formatters: ExcelEntryFormatter[] = [];

  formatters.push({
    header: intl.formatMessage({ id: `routes.excel.vehicles.columns.id` }),
    id: `stop_id`,
    formatter: (stopTaskEntry: StopTaskEntry) => stopTaskEntry.stop.id,
  });

  if (parsedTableColumns.label) {
    formatters.push({
      header: intl.formatMessage({ id: `routes.excel.vehicles.columns.name` }),
      id: `stop_label`,
      formatter: (stopTaskEntry: StopTaskEntry) => stopTaskEntry.stop.label,
    });
  }

  if (parsedTableColumns.external_id) {
    formatters.push({
      id: `stop_external_id`,
      header: intl.formatMessage({ id: `routes.excel.vehicles.columns.external_id` }),
      formatter: (stopTaskEntry: StopTaskEntry) => stopTaskEntry.stop.external_id,
    });
  }

  if (parsedTableColumns.location) {
    formatters.push({
      id: `stop_location`,
      header: intl.formatMessage({ id: `routes.excel.vehicles.columns.location` }),
      formatter: (stopTaskEntry: StopTaskEntry) =>
        stopTaskEntry.stop.location ? `${stopTaskEntry.stop.location?.lat}, ${stopTaskEntry.stop.location?.lng}` : ``,
    });
  }

  if (parsedTableColumns.location_details) {
    formatters.push({
      id: `stop_location_details`,
      header: intl.formatMessage({ id: `routes.excel.vehicles.columns.address_2` }),
      formatter: (stopTaskEntry: StopTaskEntry) => stopTaskEntry.stop.location_details,
    });
  }

  if (parsedTableColumns.comments) {
    formatters.push({
      id: `stop_comments`,
      header: intl.formatMessage({ id: `routes.excel.vehicles.columns.comments` }),
      formatter: (stopTaskEntry: StopTaskEntry) => stopTaskEntry.stop.comments,
    });
  }

  formatters.push({
    id: `task_label`,
    header: intl.formatMessage({ id: `routes.excel.vehicles.columns.task_label` }),
    formatter: (stopTaskEntry: StopTaskEntry) => stopTaskEntry.task.label,
  });

  formatters.push({
    id: `task_comments`,
    header: intl.formatMessage({ id: `routes.excel.vehicles.columns.task_comments` }),
    formatter: (stopTaskEntry: StopTaskEntry) => stopTaskEntry.task.comments,
  });

  formatters.push({
    id: `task_barcode`,
    header: intl.formatMessage({ id: `routes.excel.vehicles.columns.task_barcode` }),
    formatter: (stopTaskEntry: StopTaskEntry) => stopTaskEntry.task.barcode,
  });

  formatters.push({
    id: `task_status`,
    header: intl.formatMessage({ id: `routes.excel.vehicles.columns.task_status` }),
    formatter: (stopTaskEntry: StopTaskEntry) => stopTaskEntry.task.status,
  });

  return formatters;
};

export const exportStopsTasks = ({
  stops,
  project,
  tableColumns,
}: {
  stops: Stop[];
  project: Project;
  tableColumns?: Array<StopsTasksColumn>;
}) => {
  const wb = XLSX.utils.book_new();
  wb.Props = {
    Title: `Stops Tasks`,
    Subject: `Stops Tasks exported`,
    Author: `Routal`,
    CreatedDate: new Date(),
  };

  const stopsTasks = stopsToStopTaskEntry(stops);

  const parsedTableColumns = (tableColumns ?? []).reduce(
    (amount, next) => ({
      ...amount,
      [next.identifier]: next.displayable,
    }),
    {} as Record<StopsTasksColumnKeys, boolean>
  );

  const taskCustomFieldFormatter = (project.custom_fields?.task ?? []).map((cf) => {
    const formatter = customFieldToFormatter(cf);
    return {
      ...formatter,
      formatter: (stopTaskEntry: StopTaskEntry) =>
        formatter.formatter && formatter.formatter(stopTaskEntry.task.custom_fields),
    };
  });

  // CustomFieldFormatters always access {object}.custom_fields,
  // since now we want to deep into {object}.reports[0].custom_fields
  // we need to create a function on top of the original function
  // that will access the right custom_fields
  const taskReportCustomFieldFormatter: ExcelEntryFormatter[] = [
    ...(project.custom_fields?.service_report_task_canceled ?? []).filter((cf) =>
      project.drivers.taskCanceledHasCustomField(cf.custom_id)
    ),
    ...(project.custom_fields?.service_report_task_completed ?? []).filter((cf) =>
      project.drivers.taskCompletedHasCustomField(cf.custom_id)
    ),
  ].map((cf) => {
    const formatter = customFieldToFormatter(cf);
    return {
      ...formatter,
      formatter: (stopTaskEntry: StopTaskEntry) => {
        return (
          stopTaskEntry.task.report &&
          formatter.formatter &&
          formatter.formatter(stopTaskEntry.task.report.custom_fields)
        );
      },
    };
  });

  const allStopsFormatter = [
    ...getStopFormatters(parsedTableColumns),
    ...taskCustomFieldFormatter,
    ...taskReportCustomFieldFormatter,
  ];

  const driversSheet = jsonToExcel(stopsTasks, allStopsFormatter);

  const tasksLabel = intl.formatMessage({ id: `routes.excel.sheets.tasks` });
  wb.SheetNames.push(tasksLabel);
  wb.Sheets[tasksLabel] = driversSheet;

  return XLSX.write(wb, { bookType: `xlsx`, type: `binary`, cellDates: true });
};

const stopsToStopTaskEntry = (stops: Stop[]): StopTaskEntry[] => {
  // Each stop could have multiple tasks. We need to flatten them to have a single array of tasks with the stop info from the tableColumns displayed
  return stops.reduce((acc, stop) => {
    const stopReport = stop.getCurrentReport();
    const tasks = stop.tasks?.map((task) => {
      const taskReport = stopReport?.tasks?.find((t) => t.task_id === task.id);
      return {
        stop: {
          id: stop.id,
          label: stop.label,
          location: stop.location,
          location_details: stop.location_details,
          external_id: stop.external_id,
          comments: stop.comments,
        },
        task: {
          label: task.label,
          comments: task.comments,
          barcode: task.barcode,
          status: task.status,
          custom_fields: task.custom_fields,
          report: taskReport
            ? {
                task_id: taskReport.task_id,
                status: taskReport.status,
                custom_fields: taskReport.custom_fields,
              }
            : undefined,
        },
      };
    });
    return [...acc, ...(tasks ?? [])];
  }, [] as StopTaskEntry[]);
};
